// What a store's users see on the website. The store's own login (an account
// with no parent) sees every tab and button; the users it adds see what their
// roles allow. A store's users share the account's role ("Shipper"), so a
// check on the role alone would treat them as the owner.

export const isStoreOwner = (user) =>
  (user?.role === "Shipper" || user?.role === "oneTimeShipper") &&
  !user?.parent_id;

// The tabs inside a page (Shipments, Finances, Packages) follow the user's
// permissions for every login except a store's own.
export const limitsTabsByPermission = (user) =>
  user?.role !== "Shipper" || Boolean(user?.parent_id);

// Whether a store's user may use a store permission; the store's own login
// always may.
export const hasStorePermission = (user, name) =>
  !user?.parent_id || (user?.mergedPermissions ?? []).includes(name);

export const RUNS_MANAGE = "shipper.access.runs.manage";
export const RUNS_DRIVE = "shipper.access.runs.drive";

export const ORDER_PRICE = "shipper.access.order.price";
export const ORDER_CUSTOMER = "shipper.access.order.customer";
export const ORDER_SHIP = "shipper.access.order.ship";

// A user a store added (it shares the store's "Shipper" role): its roles
// decide what it sees. Every other login sees orders as before.
export const isStoreUser = (user) =>
  user?.role === "Shipper" && Boolean(user?.parent_id);

const storeUserHas = (user, name) =>
  !isStoreUser(user) || (user?.mergedPermissions ?? []).includes(name);

// Order prices and amounts (totals, shipping cost, fulfilment fees, costs,
// billing) need "View price". The server sends them blank to a store's users
// without it; the screens leave those columns and fields out.
export const seesOrderPrices = (user) => storeUserHas(user, ORDER_PRICE);

// The customer's phone and email need "View customer details". A driver still
// gets them on its own runs' orders, so a value the server sent is shown.
export const seesCustomerDetails = (user) => storeUserHas(user, ORDER_CUSTOMER);

// Creating a shipment shows the carriers' rates: it needs "Ship orders" and
// "View price".
export const canShipOrders = (user) =>
  seesOrderPrices(user) && storeUserHas(user, ORDER_SHIP);

// What a store's users may do to orders (Settings → Roles, Order Management).
// The server checks the same (App\Services\Access\OrderActions); every other
// login may do all of it, as before.
export const ORDER_EDIT = "shipper.access.order.edit";
export const ORDER_STATUS = "shipper.access.order.status";
export const ORDER_CANCEL = "shipper.access.order.cancel";
export const ORDER_ARCHIVE = "shipper.access.order.archive";
export const ORDER_DELETE = "shipper.access.order.delete";
export const WAREHOUSE_FULFIL = "shipper.access.warehouse.fulfil";

export const canEditOrders = (user) => storeUserHas(user, ORDER_EDIT);
export const canArchiveOrders = (user) => storeUserHas(user, ORDER_ARCHIVE);
export const canDeleteOrders = (user) => storeUserHas(user, ORDER_DELETE);

// What fulfilling sets: "Fulfil orders in the warehouse app" may set these
// from the website too.
const FULFIL_STATUSES = ["fulfilled", "attention / partially fulfilled"];

// May the user pick this fulfilment status? Cancelled needs "Cancel orders";
// any other "Change the fulfilment status", or for the two fulfilling sets,
// "Fulfil orders in the warehouse app".
export const canChooseOrderStatus = (user, statusName) => {
  const name = String(statusName ?? "")
    .trim()
    .toLowerCase();
  if (name === "cancelled") return storeUserHas(user, ORDER_CANCEL);
  if (storeUserHas(user, ORDER_STATUS)) return true;
  return FULFIL_STATUSES.includes(name) && storeUserHas(user, WAREHOUSE_FULFIL);
};

// Whether the user may pick any status at all (the status dropdowns open).
export const canChangeOrderStatus = (user) =>
  storeUserHas(user, ORDER_STATUS) ||
  storeUserHas(user, ORDER_CANCEL) ||
  storeUserHas(user, WAREHOUSE_FULFIL);

// The fulfilment statuses, each with `disabled` when the user can't pick it
// (the dropdowns grey those out).
export const withStatusChoices = (user, statuses) =>
  Array.isArray(statuses)
    ? statuses.map((status) => ({
        ...status,
        disabled: !canChooseOrderStatus(user, status?.name),
      }))
    : statuses;
